'use client';

import type { AttributesProfileUpdateProps, AuthCustomerIdentifierProps, CheckIsCustomerProps, CheckIsCustomerResponse, CustomerProps, IdentifierToRegister, RegisterError, RegisterProps, RegisterResponse, RequestOTPParams, ResetPasswordAPIResponse, SignInResponseProps, UpdateDataProps } from '@/repositories/auth/AuthRepository.types';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Dispatch, type PropsWithChildren, SetStateAction, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Flex, Spinner } from '@eqi-investimentos/react';
import { captureException } from '@sentry/nextjs';
import { authKeys } from '@/config/keys';
import { PATHS } from '@/config/paths';
import { publicRoutes } from '@/config/routesConfig';
import { SENT_NOGOALSSETTED_COOKIE_KEY } from '@/modules/FinancialPlanning/ui/Goals/PlannerHelpWidget/PlannerHelpWidget';
import { SENT_NOIMOBILIZED_COOKIE_KEY } from '@/modules/FinancialPlanning/ui/Immobilized/NoDataImmobilizedList';
import { SENT_NORETIREMENT_COOKIE_KEY } from '@/modules/FinancialPlanning/ui/Retirement/CTARetirement';
import { AuthRepository } from '@/repositories/auth/AuthRepository';
import useLocalStorage from '@/hooks/useLocalstorage';
import { AuthenticationHandler } from '@/utils/authentication';
import { destroyCookie, getCookie, setCookie } from '@/utils/cookies';
import { ONE_DAY_IN_MS, ONE_HOUR_IN_MS, ONE_MINUTE_IN_MS } from '@/utils/date';
import { atobDecode, btoaEncoded } from '@/utils/encrypt';
import { setSession } from '@/utils/jwt';
export const AuthProvider = ({
  children
}: Readonly<PropsWithChildren>) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const queryClient = useQueryClient();
  const authRepository = useMemo(() => new AuthRepository(), []);
  const authenticationHandler = new AuthenticationHandler();
  const [identifierAuth, setIdentifierAuth] = useState<Partial<AuthCustomerIdentifierProps> | null>(null);
  const [identifierToRegister, setIdentifierToRegister] = useState<IdentifierToRegister | null>(null);
  const [userRemembered, setUserRemembered] = useState<Partial<CustomerProps> | null>(null);
  const [isLogged, setIsLogged] = useState<boolean>(false);
  const [user, setUser] = useState<CustomerProps | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [hasToUpdate] = useLocalStorage<boolean>('@hasToUpdate', false);
  const [resetOtp, setResetOtp] = useState<string | undefined>();
  const getMenuDefault = useCallback(async (forced: boolean = false) => {
    try {
      const hasMenuVersion = getCookie(authKeys?.menuDatakey);
      if (hasMenuVersion && !forced) return;
      const {
        items
      } = await authRepository.getMenuVersions();
      setCookie(authKeys?.menuDatakey, '1', 60);
      window.localStorage.setItem(authKeys.menuDatakey, JSON.stringify(items));
      return items;
    } catch (error) {
      return false;
    }
  }, [authRepository]);
  const initializeAuth = async () => {
    const dataAuth = await authenticationHandler.handleAuth(searchParams, hasToUpdate);
    if (dataAuth?.customer?.name) {
      setIsLogged(true);
      setUser(dataAuth.customer);
    }
    await getMenuDefault(true);
    router.push(dataAuth?.redirect ?? PATHS.home);
    setTimeout(() => setLoading(false), 1000);
  };
  const checkIdentifierAuth = async () => {
    const fromCookie = getCookie(authKeys.authIdentifier);
    if (!fromCookie) return;
    const data = atobDecode<Partial<AuthCustomerIdentifierProps>>(fromCookie, true);
    setIdentifierAuth(data);
  };
  const checkIsCustomer = useCallback(async ({
    captcha,
    identifier
  }: CheckIsCustomerProps): Promise<CheckIsCustomerResponse | null> => {
    try {
      const {
        account: customer,
        status,
        password_login_enabled
      } = await authRepository.checkIsCustomerOnAPI({
        identifier,
        captcha
      });
      const found = ['registered', 'first_access', 'update_profile'].includes(status);
      const isFirstAccess = status === 'first_access' || !password_login_enabled;
      const hasToUpdate = status === 'update_profile';
      const identifiedCustomer: Partial<AuthCustomerIdentifierProps> = {
        ...customer,
        identifier,
        name: customer?.masked_first_name
      };
      setIdentifierAuth(identifiedCustomer);
      setCookie(authKeys.authIdentifier, btoaEncoded(identifiedCustomer), TTL);
      return {
        customer: {
          ...identifiedCustomer
        },
        found,
        isFirstAccess,
        hasToUpdate
      };
    } catch (error) {
      return null;
    }
  }, [authRepository]);
  const setLoggedUser = useCallback(async (access_token: string, refresh_token: string | null, expires_in: number) => {
    setSession(access_token);
    setCookie(authKeys.authToken, access_token, expires_in);
    if (refresh_token) {
      setCookie(authKeys.authRefreshToken, refresh_token, expires_in);
    }
    const userProfile = await authRepository.getProfile();
    await getMenuDefault();
    setUser(userProfile);
    setIsLogged(true);
    window.localStorage.setItem(authKeys.user, JSON.stringify(userProfile));
    return user;
  }, [authRepository, getMenuDefault, user]);
  const signInWithPass = useCallback(async (identifier: string, password: string): Promise<SignInResponseProps> => {
    try {
      const {
        customer: _,
        ...response
      } = await authRepository.signIn({
        identifier,
        password
      });
      const {
        access_token,
        refresh_token
      } = response;
      await setLoggedUser(access_token, refresh_token, TTL);
      const customer = await authRepository.getProfile();
      const customerToSaveCookie: CookieUser = {
        name: customer?.name,
        email: customer?.email,
        document: customer?.document,
        id: customer?.id,
        phone: customer?.phone,
        birth_date: customer?.birth_date,
        masked_email: anonymizeEmail(customer?.email),
        masked_phone: anonymizePhone(customer?.phone)
      };
      setCookie(authKeys.authCustomer, btoaEncoded(customerToSaveCookie), TTL);
      setIdentifierAuth(customerToSaveCookie);
      return {
        status: true,
        customer,
        ...response
      } as SignInResponseProps;
    } catch (error) {
      return {
        status: false,
        message: 'Erro ao realizar login'
      } as SignInResponseProps;
    }
  }, [authRepository, setLoggedUser]);
  const anonymizeEmail = (email: string) => {
    const [username, domain] = email.split('@');
    const domainParts = domain.split('.');
    const domainName = domainParts.slice(0, domainParts.length - 1).join('.');
    const domainExtension = domainParts[domainParts.length - 1];
    const usernameAnonymized = username.slice(0, 2) + '*'.repeat(username.length - 2);
    return `${usernameAnonymized}@${domainName}.${domainExtension}`;
  };
  const anonymizePhone = (phone: string) => {
    return phone.slice(0, 2) + '*'.repeat(phone.length - 2);
  };
  const getUserRemembered = (): boolean => {
    try {
      const cookieUser = getCookie(authKeys.authCustomer);
      if (!cookieUser) return false;
      const data = atobDecode<CookieUser>(cookieUser, true);
      setUserRemembered(data);
      return true;
    } catch (error) {
      return false;
    }
  };
  const handleClearDataUserRemembered = () => {
    destroyCookie(authKeys.authCustomer);
    setUserRemembered(null);
    setIdentifierAuth(null);
  };
  const requestOtpCode = useCallback(async (values: RequestOTPParams) => {
    try {
      await authRepository.requestOtpCode(values);
      return true;
    } catch (error) {
      return false;
    }
  }, [authRepository]);
  const verifyOtpCode = useCallback(async (values: {
    identifier: string;
    token: string;
  }): Promise<boolean> => {
    try {
      const result = await authRepository.verifyOtpCode(values);
      if (result?.access_token) {
        const {
          access_token,
          refresh_token
        } = result;
        await setLoggedUser(access_token, refresh_token, TTL);
        const customer = await authRepository.getProfile();
        const customerToSaveCookie: CookieUser = {
          name: customer?.name,
          email: customer?.email,
          document: customer?.document,
          id: customer?.id,
          phone: customer?.phone,
          masked_email: anonymizeEmail(customer?.email),
          masked_phone: anonymizePhone(customer?.phone)
        };
        setCookie(authKeys.authCustomer, btoaEncoded(customerToSaveCookie), TTL);
        setIdentifierAuth(customerToSaveCookie);
      }
      return true;
    } catch (error) {
      return false;
    }
  }, [authRepository, setLoggedUser]);
  const resetPassword = useCallback(async (values: {
    password: string;
    token: string;
    identifier: string;
  }): Promise<ResetPasswordAPIResponse> => {
    try {
      return await authRepository.resetPassword(values);
    } catch (error) {
      captureException(new Error(`Erro reset password auth/v1/account/password` + error));
      return {
        status: false,
        message: 'Erro ao resetar senha'
      };
    }
  }, [authRepository]);
  const updateMe = useCallback(async (attributes: AttributesProfileUpdateProps): Promise<RegisterResponse> => {
    try {
      await authRepository.updateMe(attributes);
      return {
        status: true
      };
    } catch (error) {
      captureException(new Error(`Erro update profile /me` + error));
      return {
        status: false,
        errors: (error as RegisterError)?.response?.data ? [(error as RegisterError).response!.data!] : []
      };
    }
  }, [authRepository]);
  const sendEmailLead = useCallback(async (email: string) => {
    try {
      const values = {
        url_origin: window.location.href,
        email: email
      };
      await authRepository.sendEmailLead(values);
      return true;
    } catch (error) {
      captureException(new Error(`Erro send email lead in register` + error));
      return false;
    }
  }, [authRepository]);
  const register = useCallback(async (data: RegisterProps): Promise<RegisterResponse> => {
    try {
      setSession(false);
      await authRepository.register({
        ...data
      });
      return {
        status: true
      };
    } catch (error) {
      return {
        status: false,
        errors: (error as RegisterError)?.response?.data ? [(error as RegisterError).response!.data!] : []
      };
    }
  }, [authRepository]);
  const updateData = useCallback(async (data: UpdateDataProps): Promise<RegisterResponse> => {
    try {
      setSession(false);
      await authRepository.updateData({
        ...data
      });
      return {
        status: true
      };
    } catch (error) {
      return {
        status: false,
        errors: (error as RegisterError)?.response?.data ? [(error as RegisterError).response!.data!] : []
      };
    }
  }, [authRepository]);
  const logout = useCallback(async () => {
    queryClient.clear();
    setSession(false);
    setUser(null);
    window.localStorage.removeItem(authKeys.user);
    window.localStorage.removeItem(authKeys.custom);
    window.localStorage.removeItem(authKeys.customData);
    window.localStorage.removeItem('@isChoosePlanMode');
    window.localStorage.removeItem(authKeys.accessToken);
    window.localStorage.removeItem(authKeys.menuVersions);
    window.localStorage.removeItem(authKeys?.menuDatakey);
    window.localStorage.removeItem(authKeys.videoPlaybackRate);
    destroyCookie(authKeys.authToken);
    destroyCookie(authKeys.authRefreshToken);
    destroyCookie(SENT_NORETIREMENT_COOKIE_KEY);
    destroyCookie(SENT_NOGOALSSETTED_COOKIE_KEY);
    destroyCookie(SENT_NOIMOBILIZED_COOKIE_KEY);
    destroyCookie('popup-greetings');
    destroyCookie(authKeys.authIdentifier);
    destroyCookie('menu-versions');
    destroyCookie(authKeys?.menuDatakey);
    destroyCookie('deviceCode');
    destroyCookie('lastSentDeviceTimestamp');
    window.localStorage.removeItem(authKeys.menuVersions);
    router.replace(PATHS.auth.login);
  }, [queryClient, router]);
  useEffect(() => {
    if (publicRoutes.includes(pathname)) {
      getUserRemembered();
    }
    checkIdentifierAuth();
    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const value = useMemo(() => ({
    identifierAuth,
    identifierToRegister,
    isLogged,
    resetOtp,
    user,
    userRemembered,
    checkIsCustomer,
    getUserRemembered,
    handleClearDataUserRemembered,
    logout,
    register,
    resetPassword,
    requestOtpCode,
    sendEmailLead,
    setIdentifierAuth,
    setIdentifierToRegister,
    setLoggedUser,
    setResetOtp,
    signInWithPass,
    updateMe,
    updateData,
    verifyOtpCode
  }), [checkIsCustomer, identifierAuth, identifierToRegister, isLogged, logout, register, requestOtpCode, resetOtp, resetPassword, sendEmailLead, setLoggedUser, signInWithPass, updateData, updateMe, user, userRemembered, verifyOtpCode]);
  return <AuthContext.Provider value={value} data-sentry-element="unknown" data-sentry-component="AuthProvider" data-sentry-source-file="AuthContext.tsx">
      {loading ? <Flex className="w-full h-[100vh] flex items-center justify-center" sx={{
      '.chakra-spinner': {
        color: 'brand.600 !important',
        w: '3.75rem !important',
        h: '3.75rem !important'
      }
    }}>
          <Spinner size="xl" />
        </Flex> : children}
    </AuthContext.Provider>;
};
export const useAuth = () => useContext(AuthContext);
export const AuthContext = createContext({} as AuthContextData);
const TTL = ONE_DAY_IN_MS * 3 + ONE_HOUR_IN_MS * 15 + ONE_MINUTE_IN_MS * 36;
type AuthContextData = {
  identifierAuth: Partial<AuthCustomerIdentifierProps> | CookieUser | null;
  identifierToRegister: IdentifierToRegister | null;
  userRemembered: Partial<CustomerProps> | null;
  user: CustomerProps | null;
  isLogged: boolean;
  resetOtp: string | undefined;
  checkIsCustomer: (props: CheckIsCustomerProps) => Promise<CheckIsCustomerResponse | null>;
  getUserRemembered: () => boolean;
  handleClearDataUserRemembered: () => void;
  logout: () => void;
  register: (data: RegisterProps) => Promise<RegisterResponse>;
  updateData: (data: UpdateDataProps) => Promise<RegisterResponse>;
  sendEmailLead: (email: string) => void;
  setLoggedUser: (access_token: string, refresh_token: string | null, expires_in: number) => Promise<CustomerProps | null>;
  requestOtpCode: (values: RequestOTPParams) => Promise<boolean>;
  resetPassword: (values: {
    password: string;
    token: string;
    identifier: string;
  }) => Promise<ResetPasswordAPIResponse>;
  setIdentifierAuth: Dispatch<SetStateAction<Partial<AuthCustomerIdentifierProps> | CookieUser | null>>;
  setIdentifierToRegister: Dispatch<SetStateAction<IdentifierToRegister | null>>;
  setResetOtp: (otp: string) => void;
  signInWithPass: (email: string, password: string) => Promise<SignInResponseProps>;
  updateMe: (attributes: AttributesProfileUpdateProps) => Promise<RegisterResponse>;
  verifyOtpCode: (values: {
    identifier: string;
    token: string;
  }) => Promise<boolean>;
};
type CookieUser = Pick<CustomerProps, 'id' | 'email' | 'document' | 'name' | 'phone' | 'birth_date'> & {
  masked_email: string;
  masked_phone: string;
};